import { HeartPulse } from "lucide-react";

export default function OpdPreview({ rx, patient, doctor }) {
  if (!rx || !patient) return null;
  const date = (rx.created_at || new Date().toISOString()).slice(0, 10);
  const meds = rx.medications || [];
  const dx = rx.diagnosis || [];
  const highDx = dx.filter((d) => d && String(d.likelihood || "").trim().toLowerCase() === "high" && d.condition);

  return (
    <div id="print-area" data-notranslate className="bg-white rounded-2xl shadow-xl border border-slate-200 text-slate-900 p-8 max-w-3xl mx-auto">
      {/* Letterhead */}
      <div className="flex items-start justify-between gap-4 pb-4 border-b-2 border-[#F59E0B]">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#1E5AA8] flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-amber-300" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-[#1E5AA8] leading-none">{doctor?.clinic_name || "MDvia Care"}</h2>
            <p className="text-xs text-slate-500 mt-1">{doctor?.clinic_address || ""}</p>
            {doctor?.clinic_contact && <p className="text-xs text-slate-500">{doctor.clinic_contact}</p>}
          </div>
        </div>
        <div className="text-right">
          <p className="font-display font-bold text-[#123C73]">{doctor?.full_name || doctor?.name}</p>
          <p className="text-xs text-slate-500">{doctor?.qualifications}</p>
          <p className="text-xs text-slate-500">{doctor?.specialty}</p>
          {doctor?.registration_number && <p className="text-xs text-slate-500">Reg. No: {doctor.registration_number}</p>}
        </div>
      </div>

      {/* Patient row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 bg-[#EBF3FB] rounded-xl p-3 text-sm">
        <div><span className="text-slate-500 text-xs">Patient ID</span><br /><span className="font-mono-id font-semibold text-[#123C73]">{patient.patient_id}</span></div>
        <div><span className="text-slate-500 text-xs">Name</span><br /><span className="font-semibold">{patient.name}</span></div>
        <div><span className="text-slate-500 text-xs">Age / Sex</span><br /><span className="font-semibold">{patient.age} / {patient.gender}</span></div>
        <div><span className="text-slate-500 text-xs">Date</span><br /><span className="font-semibold">{date}</span></div>
      </div>

      {rx.is_emergency && rx.emergency_referral && (
        <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-3">
          <p className="text-sm font-bold text-red-700">⚠ EMERGENCY — {rx.emergency_referral}</p>
        </div>
      )}

      <Section title="Chief Complaints" show={rx.chief_complaints?.length}>
        <ul className="list-disc pl-5 text-sm space-y-0.5">{(rx.chief_complaints || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
      </Section>

      <Section title="Provisional Diagnosis" show={highDx.length}>
        <ul className="list-disc pl-5 text-sm space-y-0.5">{highDx.map((d, i) => <li key={i}>{d.condition}</li>)}</ul>
      </Section>

      <VitalsBlock v={rx.vitals} />

      <Section title="Physical Examination" show={rx.physical_examination}>
        <p className="text-sm">{rx.physical_examination}</p>
      </Section>

      <Section title="Significant History" show={rx.significant_history}>
        <p className="text-sm">{rx.significant_history}</p>
      </Section>

      {meds.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <h3 className="text-[#1E5AA8] font-semibold text-sm mb-1.5">℞ Treatment (Generic)</h3>
          <table className="w-full text-sm border-collapse table-fixed">
            <colgroup>
              <col className="w-[4%]" /><col className="w-[30%]" /><col className="w-[13%]" /><col className="w-[15%]" /><col className="w-[14%]" /><col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="bg-[#1E5AA8] text-white text-xs">
                <th className="text-left p-2 rounded-tl-lg">#</th>
                <th className="text-left p-2">Medicine (Generic)</th>
                <th className="text-left p-2">Dose</th>
                <th className="text-left p-2">Frequency</th>
                <th className="text-left p-2">Duration</th>
                <th className="text-left p-2 rounded-tr-lg">Instructions</th>
              </tr>
            </thead>
            <tbody>
              {meds.map((m, i) => (
                <tr key={i} className="border-b border-slate-100 odd:bg-[#F7FAFF]">
                  <td className="p-2 align-top">{i + 1}</td>
                  <td className="p-2 font-medium align-top break-words">{m.name}</td>
                  <td className="p-2 align-top break-words">{m.dose}</td>
                  <td className="p-2 align-top break-words">{m.frequency}</td>
                  <td className="p-2 align-top break-words">{m.duration}</td>
                  <td className="p-2 text-slate-500 align-top break-words">{m.instructions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Section title="Diagnostic Tests Advised" show={rx.tests?.length}>
        <ul className="list-disc pl-5 text-sm space-y-0.5">{(rx.tests || []).map((t, i) => <li key={i}>{typeof t === "object" ? t.name : t}</li>)}</ul>
      </Section>

      <Section title="Advice & Counseling" show={rx.counseling?.length}>
        <ul className="list-disc pl-5 text-sm space-y-0.5">{(rx.counseling || []).map((c, i) => <li key={i}>{c}</li>)}</ul>
      </Section>

      <Section title="Referral" show={rx.referral_specialist}>
        <p className="text-sm">Referred to: <b>{rx.referral_specialist}</b>{rx.referral_reason ? ` — ${rx.referral_reason}` : ""}</p>
      </Section>

      <Section title="Additional Notes" show={rx.additional_notes}>
        <p className="text-sm">{rx.additional_notes}</p>
      </Section>

      {rx.follow_up_date && (
        <p className="mt-4 text-sm"><span className="font-semibold text-[#1E5AA8]">Follow-up Date:</span> {rx.follow_up_date}</p>
      )}

      <div className="mt-12 flex justify-end">
        <div className="text-center">
          <div className="flex items-end justify-center gap-3 min-h-[48px]">
            {doctor?.stamp_image && <img src={doctor.stamp_image} alt="stamp" className="h-14 object-contain" />}
            {doctor?.signature_image && <img src={doctor.signature_image} alt="signature" className="h-12 object-contain" />}
          </div>
          <div className="w-44 border-t border-slate-400 pt-1 mt-1" />
          <p className="text-sm font-semibold">{doctor?.full_name || doctor?.name}</p>
          <p className="text-xs text-slate-500">{doctor?.qualifications}</p>
          <p className="text-xs text-slate-400">Signature & Stamp</p>
        </div>
      </div>
    </div>
  );
}

function VitalsBlock({ v }) {
  if (!v) return null;
  const items = [
    ["bp", "BP"], ["pulse", "Pulse"], ["rr", "RR"], ["spo2", "SpO₂ %"],
    ["temp", "Temp"], ["height", "Height"], ["weight", "Weight"],
  ].filter(([k]) => v[k]);
  if (items.length === 0) return null;
  return (
    <div className="mt-4">
      <h3 className="text-[#1E5AA8] font-semibold text-sm mb-1">Vital Signs</h3>
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        {items.map(([k, label]) => <span key={k}><span className="text-slate-500">{label}:</span> <b>{v[k]}</b></span>)}
      </div>
    </div>
  );
}

function Section({ title, show, children }) {
  if (!show) return null;
  return (
    <div className="mt-4">
      <h3 className="text-[#1E5AA8] font-semibold text-sm mb-1">{title}</h3>
      {children}
    </div>
  );
}
